import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  Button,
  Typography,
  Tag,
  Avatar,
  Breadcrumb,
  Rate,
  Input,
  Pagination,
  message,
  Progress,
  Divider,
  Modal,
  Skeleton,
  Image
} from 'antd';
import {
  HomeOutlined,
  ShareAltOutlined,
  HeartOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MessageOutlined,
  TeamOutlined,
  DollarOutlined,
  ExpandOutlined,
  StarFilled,
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  CalendarOutlined,
  CustomerServiceOutlined,
  SoundOutlined,
  PictureOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons';
import './Services.css';
import parse from "html-react-parser"
import DOMPurify from 'dompurify'
import DateLine from '../../components/DateLine/DateLine';
import BookingModal from './BookingModal';
import { isAuth } from '../../utils/auth';
import { useChatStore } from '../../stores/chatStore';
import { useAuthStore } from '../../stores/authStore';
import ChatDrawer from '../../components/Chat/ChatDrawer';
import api from '../../api/axios';

const { TextArea } = Input;

const { Title, Text } = Typography;

const IMG_BASE = process.env.REACT_APP_URL;

const formatPrice = (price) => {
  if (!price) return '0';
  return Number(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const Services = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [relatedData, setRelatedData] = useState(null);
  let [mainimg, setMainImg] = useState("")
  
  const [chatOpen, setChatOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const { token, isAuthenticated, user } = useAuthStore();

  // Review states
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [reviewPage, setReviewPage] = useState(1);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const { createConversation, fetchConversations, openConversation, connect } = useChatStore();

  const handleStartChat = async () => {
    const authToken = token || localStorage.getItem('token') || localStorage.getItem('access_token');
    if (!authToken) {
      navigate('/auth');
      return;
    }
    if (!service?.business?.users_id) return;
    connect(authToken);
    const conv = await createConversation(service.business.users_id, service.id, authToken);
    if (conv) {
      openConversation(conv, authToken);
      setChatOpen(true);
    }
  };

  const fetchReviews = (page = 1) => {
    api.get(`/public/services/${id}/comments?page=${page}&per_page=10`).then(res => {
      const data = res.data?.data || res.data;
      setReviews(data.comments?.data || data.comments || []);
      setReviewStats(data.stats || null);
      setReviewTotal(data.comments?.total || data.total || 0);
      setReviewPage(data.comments?.current_page || page);
    }).catch(() => { });
  };

  useEffect(() => {
    api.get('/public/services/all-details/' + id).then(res => {
      const svc = res.data?.data?.service || res.data?.service || res.data;
      const related = res.data?.data?.related_data || null;
      setService(svc);
      setRelatedData(related);
      if (svc?.images?.[0]?.file_path) {
        setMainImg(process.env.REACT_APP_URL + svc.images[0].file_path)
      }
    }).catch(() => { })
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Computed: flatten all event_types from halls as "extras"
  const allEventTypes = useMemo(() => {
    if (!service?.halls?.length) return [];
    return service.halls.flatMap(h =>
      (h.event_types || []).map(et => ({
        id: et.id,
        title: h.halls?.length > 1 ? `${h.name} — ${et.name}` : et.name,
        name: et.name,
        price: et.price,
        halls_id: et.halls_id,
        hall_name: h.name,
      }))
    );
  }, [service]);

  // Computed: total places & area from halls
  const totalPlaces = useMemo(() => {
    return service?.halls?.reduce((sum, h) => sum + (h.places || 0), 0) || 0;
  }, [service]);

  const totalArea = useMemo(() => {
    return service?.halls?.reduce((sum, h) => sum + (h.area || 0), 0) || 0;
  }, [service]);

  const priceRanges = relatedData?.price_ranges?.event_types;
  const currency = priceRanges?.currency || 'UZS';
  const isRestaurant = service?.service_type?.id === 1 || service?.vendor_types_id === 1;
  const isSinger = service?.service_type?.id === 3 || service?.vendor_types_id === 3;

  // Computed: service tariffs (Service ichidagi tariflar)
  const serviceTariffs = useMemo(() => service?.service_tariffs || [], [service]);

  // Computed: tariflarda ko'rsatilgan taomlar (takrorlanmas)
  const tariffFoods = useMemo(() => {
    const map = new Map();
    serviceTariffs.forEach(t => (t.foods || []).forEach(f => {
      if (f.foods_id != null && !map.has(f.foods_id)) {
        map.set(f.foods_id, { id: f.foods_id, name: f.name, price: f.price, img: f.img, note: f.note });
      }
    }));
    return [...map.values()];
  }, [serviceTariffs]);

  // Computed: foods (taomlar) — tariflardagi taomlar, bo'lmasa biznes taomlari
  const foodsList = useMemo(() => {
    if (tariffFoods.length > 0) return tariffFoods;
    return service?.foods || [];
  }, [service, tariffFoods]);

  // Computed: unique genres from songs
  const uniqueGenres = useMemo(() => {
    if (!service?.songs?.length) return [];
    const map = new Map();
    service.songs.forEach(s => {
      if (s.genre && !map.has(s.genre.id)) map.set(s.genre.id, s.genre.name);
    });
    return [...map.values()];
  }, [service]);

  const handleSubmitReview = () => {
    if (!isAuth()) { navigate('/auth'); return; }
    if (myRating === 0) { message.warning('Iltimos, baho bering'); return; }
    setSubmitting(true);
    const payload = { rating: myRating, comment: myComment };

    if (editingReview) {
      api.put(`/public/reviews/${editingReview.id}`, payload).then(res => {
        message.success('Baho yangilandi');
        setEditingReview(null);
        setMyRating(0);
        setMyComment('');
        fetchReviews(reviewPage);
      }).catch(err => {
        message.error(err.response?.data?.message || 'Xatolik yuz berdi');
      }).finally(() => setSubmitting(false));
    } else {
      api.post(`/public/services/${id}/comments`, payload).then(res => {
        message.success('Baho qo\'shildi!');
        setMyRating(0);
        setMyComment('');
        fetchReviews(1);
      }).catch(err => {
        message.error(err.response?.data?.message || 'Xatolik yuz berdi');
      }).finally(() => setSubmitting(false));
    }
  };

  const handleDeleteReview = (reviewId) => {
    Modal.confirm({
      title: 'Bahoni o\'chirish',
      content: 'Rostdan ham bu bahoni o\'chirmoqchimisiz?',
      okText: 'O\'chirish',
      cancelText: 'Bekor qilish',
      okButtonProps: { danger: true },
      onOk: () => {
        api.delete(`/public/reviews/${reviewId}`).then(() => {
          message.success('Baho o\'chirildi');
          fetchReviews(reviewPage);
        }).catch(err => {
          message.error(err.response?.data?.message || 'Xatolik');
        });
      }
    });
  };

  const handleEditReview = (review) => {
    setEditingReview(review);
    setMyRating(review.rating);
    setMyComment(review.comment || '');
  };

  const cancelEdit = () => {
    setEditingReview(null);
    setMyRating(0);
    setMyComment('');
  };

  if (!service) {
    return (
      <div style={{ padding: '20px' }}>
        <Skeleton active paragraph={{ rows: 1 }} style={{ marginBottom: 16 }} />
        <div style={{ width: '100%', height: 420, borderRadius: 20, background: '#f0f0f0', marginBottom: 24 }} />
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16}><Skeleton active paragraph={{ rows: 8 }} /></Col>
          <Col xs={24} lg={8}><Skeleton active paragraph={{ rows: 4 }} /></Col>
        </Row>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 20px 40px' }}>
      {/* Breadcrumb */}
      <div className="services-header">
        <Breadcrumb
          items={[
            { href: '/', title: <HomeOutlined /> },
            { title: service.service_type?.name || 'Xizmat' },
            { title: service.name },
          ]}
        />
      </div>

      {/* ===== HERO ===== */}
      <div className="services-hero" style={{ marginBottom: 20 }}>
        <img src={mainimg} alt={service.name} className="services-hero-img" />
        <div className="services-hero-overlay" />
        <div className="services-hero-meta">
          <Title level={2} className="services-hero-title">{service.name}</Title>
          <div className="services-hero-tags">
            <Tag style={{ background: 'rgba(95,51,225,0.85)', color: '#fff', border: 'none', fontSize: 13, padding: '3px 12px', borderRadius: 20 }}>
              <EnvironmentOutlined style={{ marginRight: 4 }} />{service.service_type?.name}
            </Tag>
            {priceRanges && (
              <Tag style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', fontSize: 13, padding: '3px 12px', borderRadius: 20 }}>
                <DollarOutlined style={{ marginRight: 4 }} />
                {formatPrice(priceRanges.min)} — {formatPrice(priceRanges.max)} {currency === 'UZS' ? "so'm" : currency}
                {isRestaurant ? ' / kishi' : ''}
              </Tag>
            )}
            {service.statistics?.average_rating > 0 && (
              <Tag style={{ background: 'rgba(250,173,20,0.9)', color: '#fff', border: 'none', fontSize: 13, padding: '3px 12px', borderRadius: 20 }}>
                <StarFilled style={{ marginRight: 4 }} />{Number(service.statistics.average_rating).toFixed(1)} ({service.statistics.total_comments || 0} ta)
              </Tag>
            )}
          </div>
        </div>
        <div className="services-hero-actions">
          <button className="services-hero-btn"><ShareAltOutlined /></button>
          <button className="services-hero-btn"><HeartOutlined /></button>
        </div>
      </div>

      <Row gutter={[24, 24]}>
        {/* ===== CHAP USTUN ===== */}
        <Col xs={24} lg={16}>
          {/* Gallery thumbs */}
          <div className="gallery-thumbs">
            {(service.images || []).map((img, index) => (
              <div
                key={img.id || index}
                className={`gallery-thumb ${mainimg.includes(img.file_path) ? 'gallery-thumb-active' : ''}`}
                onClick={() => setMainImg(process.env.REACT_APP_URL + img.file_path)}
              >
                <img
                  src={process.env.REACT_APP_URL + img.file_path}
                  alt={`Rasm ${index + 1}`}
                  style={{ width: 80, height: 60, objectFit: 'cover', display: 'block' }}
                />
              </div>
            ))}
          </div>

          {/* Info cards — To'yxona uchun zallar ma'lumoti */}
          {isRestaurant && service.halls?.length > 0 && (
            <div className="info-cards">
              <div className="info-card" style={{ background: 'rgba(95,51,225,0.07)' }}>
                <div className="info-card-icon"><TeamOutlined style={{ color: '#5F33E1' }} /></div>
                <div className="info-card-value">{totalPlaces || '—'}</div>
                <div className="info-card-label">Sig'imi (kishi)</div>
              </div>
              <div className="info-card" style={{ background: 'rgba(255,77,79,0.07)' }}>
                <div className="info-card-icon"><ExpandOutlined style={{ color: '#ff4d4f' }} /></div>
                <div className="info-card-value">{totalArea || '—'}</div>
                <div className="info-card-label">Maydon (m²)</div>
              </div>
              <div className="info-card" style={{ background: 'rgba(82,196,26,0.07)' }}>
                <div className="info-card-icon"><HomeOutlined style={{ color: '#52c41a' }} /></div>
                <div className="info-card-value">{service.halls.length}</div>
                <div className="info-card-label">Zallar soni</div>
              </div>
              <div className="info-card" style={{ background: 'rgba(250,173,20,0.07)' }}>
                <div className="info-card-icon"><DollarOutlined style={{ color: '#faad14' }} /></div>
                <div className="info-card-value">{allEventTypes.length}</div>
                <div className="info-card-label">Tadbir turlari</div>
              </div>
            </div>
          )}

          {/* Info cards — Honanda uchun */}
          {isSinger && (
            <div className="info-cards">
              <div className="info-card" style={{ background: 'rgba(95,51,225,0.07)' }}>
                <div className="info-card-icon"><CustomerServiceOutlined style={{ color: '#5F33E1' }} /></div>
                <div className="info-card-value">{service.songs?.length || 0}</div>
                <div className="info-card-label">Qo'shiqlar</div>
              </div>
              <div className="info-card" style={{ background: 'rgba(255,77,79,0.07)' }}>
                <div className="info-card-icon"><SoundOutlined style={{ color: '#ff4d4f' }} /></div>
                <div className="info-card-value">{uniqueGenres.length || '—'}</div>
                <div className="info-card-label">Janrlar</div>
              </div>
              <div className="info-card" style={{ background: 'rgba(82,196,26,0.07)' }}>
                <div className="info-card-icon"><PictureOutlined style={{ color: '#52c41a' }} /></div>
                <div className="info-card-value">{service.images?.length || 0}</div>
                <div className="info-card-label">Rasmlar</div>
              </div>
              <div className="info-card" style={{ background: 'rgba(250,173,20,0.07)' }}>
                <div className="info-card-icon"><StarFilled style={{ color: '#faad14' }} /></div>
                <div className="info-card-value">{service.statistics?.average_rating || '—'}</div>
                <div className="info-card-label">Reyting</div>
              </div>
            </div>
          )}

              <DateLine serviceId={service.id} />

          {/* Zallar */}
          {service.halls?.length > 0 && (
            <div>
              <div className="section-title">
                <div className="section-title-icon" style={{ background: 'rgba(95,51,225,0.1)' }}><HomeOutlined style={{ color: '#5F33E1' }} /></div>
                Zallar
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
                {service.halls.map((hall) => (
                  <Card key={hall.id} size="small" style={{ borderRadius: 14, overflow: 'hidden' }}>
                    <Row gutter={16} align="middle">
                      {hall.image && (
                        <Col xs={24} sm={8}>
                          <Image
                            src={IMG_BASE + hall.image}
                            alt={hall.name}
                            style={{ borderRadius: 10, objectFit: 'cover', width: '100%', height: 120 }}
                          />
                        </Col>
                      )}
                      <Col xs={24} sm={hall.image ? 16 : 24}>
                        <Title level={5} style={{ marginBottom: 4 }}>{hall.name}</Title>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
                          {hall.places && (
                            <Text type="secondary"><TeamOutlined /> {hall.places} kishi</Text>
                          )}
                          {hall.area && (
                            <Text type="secondary"><ExpandOutlined /> {hall.area} m²</Text>
                          )}
                        </div>
                        {hall.event_types?.length > 0 && (
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {hall.event_types.map(et => (
                              <Tag key={et.id} color="purple" style={{ borderRadius: 8 }}>
                                {et.name}: {formatPrice(et.price)} so'm
                              </Tag>
                            ))}
                          </div>
                        )}
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Qo'shiqlar — Honanda uchun */}
          {service.songs?.length > 0 && (
            <div>
              <div className="section-title">
                <div className="section-title-icon" style={{ background: 'rgba(95,51,225,0.1)' }}><CustomerServiceOutlined style={{ color: '#5F33E1' }} /></div>
                Qo'shiqlar ({service.songs.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
                {service.songs.map((song) => {
                  const songUrl = song.file_path?.startsWith('http')
                    ? song.file_path
                    : IMG_BASE + (song.file_path?.startsWith('/') ? '' : '/') + song.file_path;
                  return (
                    <div key={song.id} style={{
                      display: 'flex', alignItems: 'center', gap: 16,
                      background: 'var(--qk-card-bg)', borderRadius: 16, padding: '16px 20px',
                      border: '1px solid #F0EAFF', transition: 'all 0.25s',
                      boxShadow: '0 2px 8px rgba(95,51,225,0.04)',
                    }}>
                      <div style={{
                        width: 52, height: 52, borderRadius: 14,
                        background: 'linear-gradient(135deg, #5F33E1, #8B5CF6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, boxShadow: '0 4px 12px rgba(95,51,225,0.25)',
                      }}>
                        <CustomerServiceOutlined style={{ color: '#fff', fontSize: 22 }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong style={{ display: 'block', fontSize: 15, marginBottom: 2 }}>{song.title}</Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          {song.theme && <Text type="secondary" style={{ fontSize: 13 }}>{song.theme}</Text>}
                          {song.genre && (
                            <Tag style={{ borderRadius: 8, background: '#EEE9FF', color: '#5F33E1', border: 'none', fontSize: 11, margin: 0 }}>
                              {song.genre.name}
                            </Tag>
                          )}
                        </div>
                      </div>
                      {song.file_path && (
                        <div style={{ flexShrink: 0 }}>
                          <audio controls preload="none" style={{ height: 36, maxWidth: 220 }}>
                            <source src={songUrl} type="audio/mpeg" />
                          </audio>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tariflar — Service ichidagi to'liq tarif kartalari */}
          {serviceTariffs.length > 0 && (
            <div>
              <div className="section-title">
                <div className="section-title-icon" style={{ background: 'rgba(95,51,225,0.1)' }}><DollarOutlined style={{ color: '#5F33E1' }} /></div>
                Tariflar ({serviceTariffs.length})
              </div>
              <div className="tariff-cards-grid">
                {serviceTariffs.map((tf) => (
                  <div key={tf.id} className="tariff-card">
                    <div className="tariff-card-head">
                      <Text strong className="tariff-card-name">{tf.name}</Text>
                      <div className="tariff-card-price">
                        <span className="tariff-card-amount">{formatPrice(tf.price)}</span>
                        <span className="tariff-card-unit">{isRestaurant ? "so'm / kishi" : "so'm"}</span>
                      </div>
                    </div>

                    {tf.note && <Text type="secondary" className="tariff-card-note">{tf.note}</Text>}

                    {tf.halls?.length > 0 && (
                      <div className="tariff-card-row">
                        <HomeOutlined style={{ color: '#5F33E1' }} />
                        <div className="tariff-card-tags">
                          {tf.halls.map((h, i) => (
                            <Tag key={i} color="purple" style={{ margin: 0 }}>
                              {h.name}{h.places ? ` · ${h.places} o'rin` : ''}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    )}

                    {tf.foods?.length > 0 && (
                      <div className="tariff-card-foods">
                        <div className="tariff-card-foods-title">
                          <ShoppingCartOutlined style={{ color: '#faad14' }} /> Kiritilgan taomlar ({tf.foods.length})
                        </div>
                        <div className="tariff-card-tags">
                          {tf.foods.map((f, i) => (
                            <Tag key={i} color="gold" style={{ margin: 0 }}>
                              {f.name}{f.quantity > 1 ? ` ×${f.quantity}` : ''}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    )}

                    <Button
                      type="primary" block className="tariff-card-btn"
                      icon={<CalendarOutlined />}
                      onClick={() => { if (isAuth()) setBookingOpen(true); else navigate('/auth'); }}
                    >
                      Shu tarifda buyurtma berish
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Taomlar (foods) — faqat restoranlarda */}
          {isRestaurant && foodsList.length > 0 && (
            <div>
              <div className="section-title">
                <div className="section-title-icon" style={{ background: 'rgba(250,173,20,0.1)' }}><ShoppingCartOutlined style={{ color: '#faad14' }} /></div>
                Taomlar ({foodsList.length})
              </div>

              <div className="taomlar-grid">
                {foodsList.map(food => (
                  <div key={food.id} className="taom-card">
                    {food.img && (
                      <img
                        src={IMG_BASE + food.img}
                        alt={food.name}
                        style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }}
                      />
                    )}
                    <div className="taom-card-info">
                      <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 2 }}>{food.name}</Text>
                      {food.note && (
                        <Text type="secondary" style={{ fontSize: 12 }}>{food.note}</Text>
                      )}
                    </div>
                    <div className="taom-card-price">
                      <Text style={{ color: '#5F33E1', fontWeight: 700, fontSize: 14 }}>
                        {formatPrice(food.price)} so'm
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Kalendar */}
          <div className="dateline-box">
            <DateLine serviceId={service.id} />
          </div>

          {/* Tavsif */}
          <div className="description-box">
            <div className="section-title">
              <div className="section-title-icon" style={{ background: 'rgba(82,196,26,0.1)' }}><EnvironmentOutlined style={{ color: '#52c41a' }} /></div>
              Xizmat ko'rsatuvchi haqida batafsil...
            </div>
            {parse(DOMPurify.sanitize(service.desc || ''))}
          </div>

          {/* Manzil */}
          {service.location?.formatted_address && (
            <div className="description-box">
              <div className="section-title">
                <div className="section-title-icon" style={{ background: 'rgba(95,51,225,0.1)' }}><EnvironmentOutlined style={{ color: '#5F33E1' }} /></div>
                Manzil
              </div>
              <Text>{service.location.formatted_address}</Text>
              {service.region && (
                <div style={{ marginTop: 8 }}>
                  <Tag>{service.region.nameUz}</Tag>
                  {service.district && <Tag>{service.district.nameUz}</Tag>}
                </div>
              )}
            </div>
          )}

          {/* ===== REVIEWS ===== */}
          <div className="reviews-section">
            <div className="section-title" style={{ fontSize: 18, marginBottom: 20 }}>
              <div className="section-title-icon" style={{ background: 'rgba(250,173,20,0.1)' }}><StarFilled style={{ color: '#faad14' }} /></div>
              Baholar va sharhlar ({service.statistics?.total_comments || 0})
            </div>

            {/* Stats */}
            {service.statistics?.average_rating > 0 && (
              <div className="review-stats">
                <div className="review-stats-left">
                  <div className="review-big-rating">{Number(service.statistics.average_rating).toFixed(1)}</div>
                  <Rate disabled allowHalf value={service.statistics.average_rating} style={{ fontSize: 16 }} />
                  <div className="review-total">{service.statistics.total_comments} ta baho</div>
                </div>
                {reviewStats && (
                  <div className="review-stats-right">
                    {[5, 4, 3, 2, 1].map(star => {
                      const count = reviewStats[`stars_${star}`] || 0;
                      const total = reviewStats.total_reviews || service.statistics.total_comments || 1;
                      const pct = Math.round((count / total) * 100);
                      return (
                        <div key={star} className="review-bar-row">
                          <span className="review-bar-label">{star} <StarFilled style={{ color: '#faad14', fontSize: 11 }} /></span>
                          <Progress percent={pct} showInfo={false} strokeColor="#faad14" trailColor="#f0f0f0" size="small" style={{ flex: 1 }} />
                          <span className="review-bar-count">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <Divider />

            {/* Baho berish formasi */}
            <div className="review-form">
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 10 }}>
                {editingReview ? 'Bahoni tahrirlash' : 'Baho bering'}
              </Text>
              <div className="review-form-inner">
                <Rate value={myRating} onChange={setMyRating} style={{ fontSize: 28 }} />
                <TextArea
                  rows={2}
                  placeholder="Izoh qoldiring (ixtiyoriy)"
                  value={myComment}
                  onChange={e => setMyComment(e.target.value)}
                  style={{ borderRadius: 10, marginTop: 10 }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={submitting}
                    onClick={handleSubmitReview}
                    disabled={myRating === 0}
                  >
                    {editingReview ? 'Yangilash' : 'Yuborish'}
                  </Button>
                  {editingReview && (
                    <Button onClick={cancelEdit}>Bekor qilish</Button>
                  )}
                </div>
              </div>
            </div>

            <Divider />

            {/* Sharhlar ro'yxati */}
            {(service.comments?.length === 0 && reviews.length === 0) ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#999' }}>
                Hali sharhlar yo'q. Birinchi bo'lib baho bering!
              </div>
            ) : (
              <div className="reviews-list">
                {(reviews.length > 0 ? reviews : service.comments || []).map(review => {
                  const isOwn = user && (user.id === review.user_id);
                  return (
                    <div key={review.id} className="review-item">
                      <div className="review-item-header">
                        <Avatar size={36} icon={<UserOutlined />} style={{ backgroundColor: '#5F33E1', flexShrink: 0 }} />
                        <div className="review-item-info">
                          <div className="review-item-name">{review.user?.fullname || review.user?.name || 'Foydalanuvchi'}</div>
                          <div className="review-item-date">
                            {review.created_at ? new Date(review.created_at).toLocaleDateString('uz-UZ', { day: 'numeric', month: 'long', year: 'numeric' }) : review.created || ''}
                          </div>
                        </div>
                        <div className="review-item-rating">
                          <Rate disabled value={review.rating} style={{ fontSize: 13 }} />
                        </div>
                      </div>
                      {review.comment && (
                        <div className="review-item-comment">{review.comment}</div>
                      )}
                      {isOwn && (
                        <div className="review-item-actions">
                          <Button size="small" type="link" icon={<EditOutlined />} onClick={() => handleEditReview(review)}>Tahrirlash</Button>
                          <Button size="small" type="link" danger icon={<DeleteOutlined />} onClick={() => handleDeleteReview(review.id)}>O'chirish</Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {reviewTotal > 10 && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Pagination
                  current={reviewPage}
                  total={reviewTotal}
                  pageSize={10}
                  onChange={(page) => { setReviewPage(page); fetchReviews(page); }}
                  size="small"
                />
              </div>
            )}
          </div>
        </Col>

        {/* ===== O'NG USTUN ===== */}
        <Col xs={24} lg={8}>
          <div className="right-sticky">
            <Card className="provider-card" variant="borderless" styles={{ body: { padding: 0 } }}>
              <div className="provider-card-header">
                <Avatar
                  size={56}
                  src={service.business?.main_image ? IMG_BASE + service.business.main_image : null}
                  icon={<UserOutlined />}
                  style={{ border: '3px solid rgba(255,255,255,0.4)', flexShrink: 0, backgroundColor: '#5F33E1' }}
                />
                <div className="provider-card-info">
                  <span className="provider-card-label">Xizmat ko'rsatuvchi</span>
                  <span className="provider-card-name">{service.business?.name}</span>
                </div>
              </div>
              <div className="provider-card-body">
                <Button icon={<PhoneOutlined />} block size="large" href={"tel:" + (service.business?.phone || '')} style={{ borderRadius: 10 }}>
                  {service.business?.phone || 'Telefon'}
                </Button>
                <Button
                  type="primary"
                  icon={<MessageOutlined />}
                  block
                  size="large"
                  onClick={handleStartChat}
                  style={{ borderRadius: 10, background: 'linear-gradient(135deg,#5F33E1,#8B5CF6)', border: 'none' }}
                >
                  Xabar yozish
                </Button>
              </div>
            </Card>
            {/* Narx va Buyurtma */}
            <Card bordered={false} style={{ borderRadius: 18, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
              <div style={{ textAlign: 'center', padding: '4px 0 12px' }}>
                {priceRanges?.min_price ? (
                  <>
                    <Text type="secondary" style={{ fontSize: 12 }}>Narx</Text>
                    <Title level={3} style={{ margin: '4px 0', color: '#5F33E1' }}>
                      {formatPrice(priceRanges.min_price)} {priceRanges.min_price !== priceRanges.max_price ? `— ${formatPrice(priceRanges.max_price)}` : ''} so'm
                    </Title>
                    {isRestaurant && <Text type="secondary" style={{ fontSize: 12 }}>kishi boshiga</Text>}
                  </>
                ) : (
                  <Text type="secondary" style={{ fontSize: 13 }}>Narxni bilish uchun buyurtma bering</Text>
                )}
              </div>
              <Button
                type="primary"
                size="large"
                block
                icon={<CalendarOutlined />}
                onClick={() => {
                  if (!isAuth()) { navigate('/auth/login'); return; }
                  setBookingOpen(true);
                }}
                style={{
                  height: 50, borderRadius: 12, fontSize: 16, fontWeight: 600,
                  background: 'linear-gradient(135deg,#5F33E1,#8B5CF6)', border: 'none',
                  boxShadow: '0 4px 16px rgba(95,51,225,0.3)'
                }}
              >
                Buyurtma berish
              </Button>
              <p style={{ fontSize: 11, textAlign: 'center', color: '#999', marginTop: 10, marginBottom: 0 }}>
                Hech qanday majburiyat yo'q — istalgan vaqtda bekor qilish mumkin
              </p>
            </Card>
          </div>
        </Col>
      </Row>
      <ChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} />
      <BookingModal
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        service={service}
        relatedData={relatedData}
        onContract={(booking) => {
          console.log('Booking created:', booking);
        }}
      />
    </div >
  );
};

export default Services;